import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { socketHelper, statusHelper } from '../helpers';
import { base64ToArrayBuffer, blobToJSON } from '../helpers/quads.helper';
import { isJSON, isObject } from 'class-validator';
import { EMesajAltTipi, EMesajTipi, ReceivedMessageType } from 'src/types/components/socket.type';
import { contentActions, contentsActions, eventActions, unitsActions } from '../redux/actions';
import { EEmirTuru, ERaporKodu } from 'src/enums/mixed.enum';
import { getEnumKeyByValue2 } from '../helpers/enum.helper';
import { closeSocketConnection } from '../helpers/socket.helper';
import { urlsConfig } from 'src/configs';

import jsonBigint from 'src/libs/JSONParser/json-bigint';
import moment from 'moment';
import { EventTimePreference } from 'src/enums/eventTimePreference.enum';
import { MINUTE, SECOND } from '../constants/time.constant';

const jsonBigAlways = jsonBigint({ useNativeBigInt: true });
type Props = {
  openNotification: (title: string, context: string, isError: boolean) => void;
};

export const useSocket = ({ openNotification }: Props) => {
  const { isSurferOn, hymotsDB, timePreference } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.eventState,
  );
  const { units } = useSelector((state: reduxRootTypes.RootStateType) => state.unitsState);

  const dispatch = useDispatch();

  const lastUpdatedMinute = useRef<number>(0);

  const calculateTimeUpdate = (gameSpeed: string, socketUnixType: number) => {
    const speed = Number(gameSpeed);

    if (speed >= 300) {
      return socketUnixType - lastUpdatedMinute.current >= 15 * MINUTE; // 15 minutes in the simulation
    } else if (speed >= 100) {
      return socketUnixType - lastUpdatedMinute.current >= 10 * MINUTE; // 10 minutes in the simulation
    } else if (speed >= 10) {
      return socketUnixType - lastUpdatedMinute.current >= 5 * MINUTE; // 5 minutes in the simulation
    } else {
      return socketUnixType - lastUpdatedMinute.current >= 1 * MINUTE; // 1 minutes in the simulation
    }
  };

  const socketListenerHandler = useCallback(() => {
    if (isSurferOn) {
      socketHelper.listenSocket(async (event: any) => {
        try {
          const { data } = event;

          let parsedData = data;

          if (parsedData.zipliMi && parsedData.icerik) {
            parsedData = JSON.parse(data);
            const arrayBuffer = base64ToArrayBuffer(parsedData.icerik);

            parsedData.icerik = await blobToJSON(new Blob([arrayBuffer]));
          } else {
            parsedData = jsonBigAlways.parse(data);
          }

          if (isJSON(data)) {
            const genericMessage: ReceivedMessageType = parsedData;

            if (parsedData.icerik === '' || parsedData.icerik.durum === 1) {
              lastUpdatedMinute.current = 0;
            }

            if (genericMessage.mesajTipi === 1 && genericMessage.mesajAltTipi === 3) {
              closeSocketConnection(Number(hymotsDB?.toString()));
              lastUpdatedMinute.current = 0;
            } else {
              switch (genericMessage.mesajTipi) {
                // Genel Mesajlar
                case EMesajTipi.GENEL:
                  // EtmenGuncelleme
                  if (isObject(genericMessage?.icerik)) {
                    if (genericMessage.mesajAltTipi === EMesajAltTipi.ARKAYUZ_HAZIR_DEGIL) {
                      lastUpdatedMinute.current = 0;
                    }

                    if (
                      genericMessage.mesajAltTipi === EMesajAltTipi.SIMULASYON_TANITIM_GUNCELLEMESI_2D ||
                      genericMessage.mesajAltTipi === EMesajAltTipi.SIMULASYON_DURUM_GUNCELLEMESI_2D
                    ) {
                      if (timePreference === EventTimePreference.Simulation) {
                        const socketUnixType = Number(genericMessage.icerik.zaman.split(',')[0]);
                        const gameSpeed = Number(genericMessage.icerik.zaman.split(',')[3]).toFixed(0);

                        const dateUTC = moment(socketUnixType).utc().format('YYYY-MM-DD HH:mm:ss');

                        // Update time state according to game speed
                        if (calculateTimeUpdate(gameSpeed, socketUnixType)) {
                          lastUpdatedMinute.current = socketUnixType;
                          dispatch(eventActions.eventUnixTimeChanged(moment(dateUTC, 'YYYY-MM-DD HH:mm:ss').valueOf()));
                        }
                      }

                      dispatch(
                        unitsActions.updateUnitsFromBackend(
                          genericMessage.icerik.etmenler, // responseUnits
                          genericMessage.mesajAltTipi, // mesajAltTipi
                          genericMessage.icerik.olaylar, // olaylar
                          genericMessage.icerik.etmenTuru, // prototypes,
                        ),
                      );
                    }
                  }
                  break;
                case 2:
                  // EmirCevap

                  if (genericMessage.icerik !== '') {
                    // Stop spinning when failed to load database
                    if (
                      genericMessage.icerik.raporKodu !== undefined &&
                      genericMessage.icerik.raporKodu === ERaporKodu['FAILED TO LOAD DATABASE']
                    ) {
                      // dispatch(gameActions.gameStateChanged(GameState.Default));
                    }
                    if (genericMessage.icerik.raporKodu !== undefined && genericMessage.icerik.raporKodu >= 0) {
                      dispatch(
                        contentActions.addCommandResult({
                          successfulCommands: [genericMessage.icerik.emirId.toString()],
                          failedCommands: [],
                        }),
                      );

                      openNotification(
                        'Command Verified',
                        `${
                          genericMessage.icerik.emirTuru !== EEmirTuru.EMPTY
                            ? getEnumKeyByValue2(EEmirTuru, genericMessage.icerik.emirTuru) || ''
                            : ''
                        } ${getEnumKeyByValue2(ERaporKodu, genericMessage.icerik.raporKodu) || ''}`,
                        false,
                      );

                      // dispatch(gameActions.gameStateChanged(GameState.Active));
                    } else if (genericMessage.icerik.raporKodu === 0) {
                      // dispatch(gameActions.gameDateChanged(genericMessage.icerik.zaman * 1000));
                    } else {
                      dispatch(
                        contentActions.addCommandResult({
                          successfulCommands: [],
                          failedCommands: [genericMessage.icerik.emirId.toString()],
                        }),
                      );

                      openNotification(
                        'Command Failed',
                        `${
                          genericMessage.icerik.emirTuru !== EEmirTuru.EMPTY
                            ? getEnumKeyByValue2(EEmirTuru, genericMessage.icerik.emirTuru) || ''
                            : ''
                        } ${getEnumKeyByValue2(ERaporKodu, genericMessage.icerik.raporKodu) || ''} ${
                          genericMessage.icerik.aciklama
                        }`,
                        true,
                      );
                    }
                  }

                  break;
                case 3:
                  // SurferInfo
                  statusHelper.displayErrorMessage(genericMessage.icerik);
                  break;
                case 4:

                case 10:
                  //error message condition:
                  if (genericMessage.mesajAltTipi === 24) {
                    dispatch(contentsActions.experimentationReportSet(genericMessage));
                  }
                  break;
                case EMesajTipi.HLA:
                  if (
                    genericMessage.mesajAltTipi === EMesajAltTipi.LRC_RTI_BAGLANTISI_TAMAMLANDI ||
                    genericMessage.mesajAltTipi === EMesajAltTipi.FEDERASYON_OLUSTURMA_BASARISIZ ||
                    genericMessage.mesajAltTipi === EMesajAltTipi.LRC_RTI_BAGLANTISI_BASARISIZ ||
                    genericMessage.mesajAltTipi === EMesajAltTipi.FEDERASYONA_KATILMA_BASARISIZ ||
                    genericMessage.mesajAltTipi === EMesajAltTipi.LRC_RTI_BAGLANTISI_KULLANICI_TARAFINDAN_KESILDI
                  ) {
                    dispatch(eventActions.hlaContentChanged(genericMessage));
                  } else if (genericMessage.mesajAltTipi === EMesajAltTipi.LRC_ENTITY_TO_REFLECT) {
                    dispatch(unitsActions.addHlaUnit(genericMessage.icerik));
                  } else if (genericMessage.mesajAltTipi === EMesajAltTipi.LRC_ENTITIES_TO_REFLECT) {
                    dispatch(unitsActions.setHlaUnits(genericMessage.icerik));
                  }

                  break;
                default:
              }
            }
          } else {
          }
        } catch (error) {
          console.error('Failed to read data from surfer', error);
        }
      });
    }
  }, [isSurferOn, units]);

  return {
    socketListenerHandler,
    openNotification,
  };
};
